import { Howl, Howler } from "howler";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useJobsContext } from "./JobsContexts";

interface AudioContextType {
  audioState: string;
  isEnabled: boolean;
  isPending: boolean;
  isSuspended: boolean;
  hasError: boolean;
  playSound: (fileName: string) => Howl | null;
  playNotificationLoop: (fileName: string) => Howl | null;
  stopNotificationLoop: () => void;
  shutdownAudio: () => void;
}

const AUDIO_STATES = {
  UNINITIALIZED: "uninitialized",
  PENDING: "pending",
  ENABLED: "enabled",
  SUSPENDED: "suspended",
  ERROR: "error",
};

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const AudioProvider = ({ children }: { children: React.ReactNode }) => {
  const { jobs, isLoading: jobsLoading } = useJobsContext();
  const [audioState, setAudioState] = useState(AUDIO_STATES.UNINITIALIZED);
  const [isUserInteracted, setIsUserInteracted] = useState(false);
  const [needsUserGesture, setNeedsUserGesture] = useState(false);

  const jobLoopRef = useRef<Howl | null>(null);
  const notificationLoopRef = useRef<Howl | null>(null);
  const activeSoundsRef = useRef<Set<Howl>>(new Set());
  const volumeUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const wasEnabledRef = useRef(false);
  const prevPendingIdsRef = useRef<Set<string>>(new Set());

  // Cleanup
  const cleanupSound = useCallback((sound: Howl) => {
    try {
      sound.stop();
      sound.unload();
      activeSoundsRef.current.delete(sound);
    } catch (err) {
      console.error("Error cleaning up sound:", err);
    }
  }, []);

  const checkAudioState = useCallback(() => {
    try {
      const ctx = Howler.ctx;
      if (!ctx) {
        setAudioState(AUDIO_STATES.UNINITIALIZED);
        setNeedsUserGesture(false);
        return;
      }

      const prevState = audioState;

      switch (ctx.state) {
        case "running":
          if (audioState !== AUDIO_STATES.ENABLED) {
            setAudioState(AUDIO_STATES.ENABLED);
            wasEnabledRef.current = true;
            setNeedsUserGesture(false);
          }
          break;
        case "suspended":
          if (wasEnabledRef.current && prevState === AUDIO_STATES.ENABLED) {
            console.warn(
              "AudioContext unexpectedly suspended, user gesture needed",
            );
            setNeedsUserGesture(true);
          }

          if (isUserInteracted) {
            setAudioState(AUDIO_STATES.SUSPENDED);
          } else {
            setAudioState(AUDIO_STATES.PENDING);
          }
          break;
        case "closed":
          setAudioState(AUDIO_STATES.ERROR);
          setNeedsUserGesture(false);
          wasEnabledRef.current = false;
          break;
        default:
          if (!isUserInteracted) {
            setAudioState(AUDIO_STATES.PENDING);
            setNeedsUserGesture(false);
          }
          break;
      }
    } catch (err) {
      console.error("Audio state check error:", err);
      setAudioState(AUDIO_STATES.ERROR);
      setNeedsUserGesture(false);
      wasEnabledRef.current = false;
    }
  }, [audioState, isUserInteracted]);

  const enableAudio = useCallback(async (): Promise<boolean> => {
    let timeoutId: number | null = null;

    try {
      setIsUserInteracted(true);
      setNeedsUserGesture(false);

      if (Howler.ctx && Howler.ctx.state === "suspended") {
        await Howler.ctx.resume();
      }

      // iOS unlock
      if (/iPhone|iPad|iPod|Safari/i.test(navigator.userAgent)) {
        const dummySound = new Howl({
          src: [
            "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA/+M4wAAAAAAAAAAAAEluZm8AAAAPAAAAAwAAAbAAqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV////////////////////////////////////////////AAAAAExhdmM1OC4xMwAAAAAAAAAAAAAAACQDkAAAAAAAAAGw9wrNaQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+MYxAAAAANIAAAAAExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/+MYxDsAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/+MYxHYAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV",
          ],
          volume: 0,
          html5: false,
        });
        dummySound.play();
        dummySound.unload();
      }

      timeoutId = window.setTimeout(checkAudioState, 100);

      return true;
    } catch (err) {
      console.error("Error enabling audio:", err);
      setAudioState(AUDIO_STATES.ERROR);
      setNeedsUserGesture(false);
      return false;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }, [checkAudioState]);

  const resumeAudio = useCallback(async (): Promise<boolean> => {
    if (audioState === AUDIO_STATES.SUSPENDED || needsUserGesture) {
      return await enableAudio();
    }
    return false;
  }, [audioState, needsUserGesture, enableAudio]);

  // Dinamikus hangerő állítás
  const updateVolumes = useCallback(() => {
    const jobRunning = jobLoopRef.current?.playing();
    const notificationRunning = notificationLoopRef.current?.playing();

    if (jobRunning && notificationRunning) {
      jobLoopRef.current?.volume(0);
      notificationLoopRef.current?.volume(1);
    } else if (jobRunning) {
      jobLoopRef.current?.volume(1.0);
    } else if (notificationRunning) {
      notificationLoopRef.current?.volume(1.0);
    }
  }, []);

  const scheduleVolumeUpdate = useCallback(() => {
    // Előző timeout törlése
    if (volumeUpdateTimeoutRef.current) {
      clearTimeout(volumeUpdateTimeoutRef.current);
    }

    // Új timeout beállítása
    volumeUpdateTimeoutRef.current = setTimeout(() => {
      updateVolumes();
      volumeUpdateTimeoutRef.current = null;
    }, 50);
  }, [updateVolumes]);

  const playSound = useCallback(
    (fileName: string): Howl | null => {
      if (audioState !== AUDIO_STATES.ENABLED) {
        console.warn("Audio not enabled, cannot play sound");
        return null;
      }

      try {
        const sound = new Howl({
          src: [`/${fileName}`],
          volume: 1,
          onloaderror: (_, err) => {
            console.error("Sound load error:", err);
            activeSoundsRef.current.delete(sound);
          },
          onplayerror: (_, err) => {
            console.error("Sound play error:", err);
            activeSoundsRef.current.delete(sound);
            if (Howler.ctx && Howler.ctx.state === "suspended") {
              setNeedsUserGesture(true);
              checkAudioState();
            }
          },
        });

        activeSoundsRef.current.add(sound);

        sound.once("end", () => {
          activeSoundsRef.current.delete(sound);
        });

        sound.once("stop", () => {
          activeSoundsRef.current.delete(sound);
        });

        sound.play();
        return sound;
      } catch (err) {
        console.error("Error creating sound:", err);
        return null;
      }
    },
    [audioState, checkAudioState],
  );

  // Job loop kezelése
  const playJobLoop = useCallback(
    (fileName: string) => {
      if (audioState !== AUDIO_STATES.ENABLED) {
        console.warn("Audio not enabled, cannot play job loop");
        return null;
      }

      try {
        // Előző job loop leállítása
        if (jobLoopRef.current) {
          cleanupSound(jobLoopRef.current);
          jobLoopRef.current = null;
        }

        const loopSound = new Howl({
          src: [`/${fileName}`],
          loop: true,
          volume: 1,
          onplay: () => {
            scheduleVolumeUpdate();
          },
          onend: () => {
            activeSoundsRef.current.delete(loopSound);
            scheduleVolumeUpdate();
          },
          onstop: () => {
            activeSoundsRef.current.delete(loopSound);
            scheduleVolumeUpdate();
          },
        });

        jobLoopRef.current = loopSound;
        activeSoundsRef.current.add(loopSound);
        loopSound.play();

        return loopSound;
      } catch (err) {
        console.error("playJobLoop error", err);
        return null;
      }
    },
    [audioState, cleanupSound, scheduleVolumeUpdate],
  );

  const stopJobLoop = useCallback(() => {
    if (jobLoopRef.current) {
      cleanupSound(jobLoopRef.current);
      jobLoopRef.current = null;
      scheduleVolumeUpdate();
    }
  }, [cleanupSound, scheduleVolumeUpdate]);

  // Notification loop kezelése (külön a notificationök számára)
  const playNotificationLoop = useCallback(
    (fileName: string): Howl | null => {
      if (audioState !== AUDIO_STATES.ENABLED) {
        console.warn("Audio not enabled, cannot play notification loop");
        return null;
      }

      try {
        // Előző notification loop leállítása
        if (notificationLoopRef.current) {
          cleanupSound(notificationLoopRef.current);
          notificationLoopRef.current = null;
        }

        const loopSound = new Howl({
          src: [`/${fileName}`],
          loop: true,
          volume: 1,
          onplay: () => {
            scheduleVolumeUpdate();
          },
          onend: () => {
            activeSoundsRef.current.delete(loopSound);
            scheduleVolumeUpdate();
          },
          onstop: () => {
            activeSoundsRef.current.delete(loopSound);
            scheduleVolumeUpdate();
          },
        });

        notificationLoopRef.current = loopSound;
        activeSoundsRef.current.add(loopSound);
        loopSound.play();

        return loopSound;
      } catch (err) {
        console.error("playNotificationLoop error", err);
        return null;
      }
    },
    [audioState, cleanupSound, scheduleVolumeUpdate],
  );

  const stopNotificationLoop = useCallback(() => {
    if (notificationLoopRef.current) {
      cleanupSound(notificationLoopRef.current);
      notificationLoopRef.current = null;
      scheduleVolumeUpdate();
    }
  }, [cleanupSound, scheduleVolumeUpdate]);

  const shutdownAudio = useCallback(() => {
    if (volumeUpdateTimeoutRef.current) {
      clearTimeout(volumeUpdateTimeoutRef.current);
      volumeUpdateTimeoutRef.current = null;
    }

    stopJobLoop();
    stopNotificationLoop();

    activeSoundsRef.current.forEach((sound) => {
      cleanupSound(sound);
    });
    activeSoundsRef.current.clear();

    prevPendingIdsRef.current = new Set();
    setAudioState(AUDIO_STATES.UNINITIALIZED);
    setIsUserInteracted(false);
    setNeedsUserGesture(false);
  }, [stopJobLoop, stopNotificationLoop, cleanupSound]);

  // Hang logika
  useEffect(() => {
    if (jobsLoading || audioState !== AUDIO_STATES.ENABLED) return;

    const ACTIVE_STATES = ["started", "arrived", "accepted"];

    const activeJobs = (jobs || []).filter((job) =>
      ACTIVE_STATES.includes(job["own-status"].state),
    );
    const pendingJobs = (jobs || []).filter(
      (job) => !ACTIVE_STATES.includes(job["own-status"].state),
    );

    const hasActive = activeJobs.length > 0;
    const hasPending = pendingJobs.length > 0;

    const currentPendingIds = new Set(pendingJobs.map((j) => j.id));
    const prevPendingIds = prevPendingIdsRef.current;

    // új pending
    const hasNewPending = [...currentPendingIds].some(
      (id) => !prevPendingIds.has(id),
    );

    if (!hasActive && hasPending) {
      if (!jobLoopRef.current) {
        playJobLoop("siren-loop.mp3");
      }
    } else if (hasActive) {
      stopJobLoop();

      if (hasNewPending) {
        playSound("siren-loop.mp3");
      }
    } else {
      stopJobLoop();
    }

    prevPendingIdsRef.current = currentPendingIds;
  }, [jobsLoading, audioState, playJobLoop, stopJobLoop, playSound, jobs]);

  // Automatikus user gesture listener
  useEffect(() => {
    if (!needsUserGesture) return;

    const handleUserGesture = () => {
      resumeAudio();
    };

    const events = ["click", "touchstart", "keydown", "touchend"];
    events.forEach((event) => {
      window.addEventListener(event, handleUserGesture, {
        passive: true,
        once: true,
      });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserGesture);
      });
    };
  }, [needsUserGesture, resumeAudio]);

  // Audio state monitoring
  useEffect(() => {
    checkAudioState();

    let timeoutId: number | null = null;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        timeoutId = window.setTimeout(checkAudioState, 100);
      }
    };
    const handleFocus = () => {
      timeoutId = window.setTimeout(checkAudioState, 100);
    };
    const handleStateChange = () => {
      checkAudioState();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    if (Howler.ctx) {
      Howler.ctx.addEventListener("statechange", handleStateChange);
    }

    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      window.addEventListener("pageshow", handleFocus);
      window.addEventListener("pagehide", checkAudioState);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      if (Howler.ctx) {
        Howler.ctx.removeEventListener("statechange", handleStateChange);
      }
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        window.removeEventListener("pageshow", handleFocus);
        window.removeEventListener("pagehide", checkAudioState);
      }
    };
  }, [checkAudioState]);

  // Kezdeti user gesture kezelése
  useEffect(() => {
    if (isUserInteracted) return;

    const handleUserGesture = () => {
      enableAudio();
    };

    window.addEventListener("click", handleUserGesture);
    window.addEventListener("touchstart", handleUserGesture, { passive: true });

    return () => {
      window.removeEventListener("click", handleUserGesture);
      window.removeEventListener("touchstart", handleUserGesture);
    };
  }, [isUserInteracted, enableAudio]);

  const contextValue: AudioContextType = {
    audioState,
    isEnabled: audioState === AUDIO_STATES.ENABLED,
    isPending: audioState === AUDIO_STATES.PENDING,
    isSuspended: audioState === AUDIO_STATES.SUSPENDED,
    hasError: audioState === AUDIO_STATES.ERROR,
    playSound,
    playNotificationLoop,
    stopNotificationLoop,
    shutdownAudio,
  };

  return (
    <AudioContext.Provider value={contextValue}>
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudio must be used within an AudioProvider");
  }
  return context;
};
